import type React from "react";
import { useTranslation } from "react-i18next";

import type { ModuleSection, ModuleSubject } from "models/product/ModuleDetails";

import usePersistantCollapsedModuleSection from "hooks/usePersistantCollapsedModuleSection";

import { Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssIcon } from "styles/helpers/layout";

import { useCallback, useState } from "react";
import { AdaptiveSubject } from "../../../ModuleDetailsPage/components/AdaptiveSubject/AdaptiveSubject";
import { WritingSubject } from "../../../ModuleDetailsPage/components/WritingSubject/WritingSubject";
import { StyledSectionCollapsible } from "./CollapsibleModuleSection.styled";

interface CollapsibleModuleSectionProps {
  moduleId: number;
  productId: number;
  section: ModuleSection;
}

export const CollapsibleModuleSection: React.FC<CollapsibleModuleSectionProps> = ({
  section,
  moduleId,
  productId,
}) => {
  const { t } = useTranslation("homepage");
  const {
    isCollapsedModuleSection,
    addToCollapsedModuleSections,
    removeFromCollapsedModuleSections,
  } = usePersistantCollapsedModuleSection();

  // Assign initial collapsed state based on whether the section id is present in the storage or not
  const initialCollapsedState = section.id ? isCollapsedModuleSection(section.id) : false;

  const [isCollapsed, setIsCollapsed] = useState(initialCollapsedState);

  const toggleCollapsibleModuleSection = useCallback(() => {
    if (!section.id) {
      return;
    }

    const shouldCollapse = !isCollapsed;
    setIsCollapsed(shouldCollapse);

    // Add or remove section from local storage depending on whether the user expanded or collapsed it
    if (shouldCollapse) {
      addToCollapsedModuleSections(section.id);
    } else {
      removeFromCollapsedModuleSections(section.id);
    }
  }, [section, isCollapsed]);

  return (
    <section key={section.id} data-cy="module-section-item">
      {section.title && (
        <StyledSectionCollapsible
          aria-expanded={!isCollapsed}
          aria-controls={`module-section-${section.id}-subjects`}
          alignItems="center"
          flexDirection="row"
          columnGap={2}
          marginBottom="1.5rem"
          role="button"
          onClick={toggleCollapsibleModuleSection}
        >
          <CssIcon iconName={`far fa-chevron-${isCollapsed ? "down" : "up"}`} />
          <Heading3 data-cy="module-section-title">{section.title}</Heading3>
          <ParagraphLarge>
            {t("moduleSection.subjectCount", { count: section.subjects.length })}
          </ParagraphLarge>
        </StyledSectionCollapsible>
      )}
      <section
        data-cy="module-section-subjects-container"
        id={`module-section-${section.id}-subjects`}
        hidden={isCollapsed}
      >
        {section.subjects.map((subject: ModuleSubject) =>
          subject.type === "adaptive" ? (
            <AdaptiveSubject
              key={subject.id}
              moduleId={+moduleId}
              productId={+productId}
              subject={subject}
            />
          ) : (
            <WritingSubject key={subject.id} subject={subject} />
          ),
        )}
      </section>
    </section>
  );
};
